import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, User, Mail, Phone, Building, Globe, MessageSquare, ChevronRight, ChevronLeft } from 'lucide-react';
import { insertConsultationRequest, type ConsultationRequest } from '../lib/supabase';
import { trackMetaPixelLead, trackMetaPixelContact } from '@/lib/meta-pixel';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '@/components/Seo';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, useLocalizedPath, withLanguagePrefix } from '@/lib/locale';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  message: string;
  services: string[];
  budget: string;
  timeline: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  services?: string;
  [key: string]: string | undefined;
}

type StepId = 1 | 2 | 3 | 4;

const STEPS: StepId[] = [1, 2, 3, 4];

const ConsultationPage: React.FC = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const localizedPath = useLocalizedPath();
  const locale = language === 'en' ? 'en' : 'el';
  const isGreek = locale === 'el';
  const meta = getPageMeta('consultation', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  
  const copy = {
    submitError: isGreek
      ? 'Υπήρξε ένα σφάλμα κατά την αποστολή της αίτησης. Παρακαλώ δοκιμάστε ξανά.'
      : 'There was an error submitting your request. Please try again.',
    formSubtitleFallback: isGreek
      ? 'Συμπληρώστε τη φόρμα και θα σας καλέσουμε σε 24 ώρες'
      : 'Fill out the form and we will call you within 24 hours',
    step1Title: isGreek ? 'Επικοινωνία' : 'Contact',
    step1Subtitle: isGreek ? 'Πώς μπορούμε να επικοινωνήσουμε μαζί σας' : 'How can we reach you',
    step2Title: isGreek ? 'Υπηρεσίες' : 'Services',
    endSubtitle: isGreek ? 'Τι είδους υπηρεσίες χρειάζεστε' : 'What services do you need',
    step3Title: isGreek ? 'Λεπτομέρειες' : 'Details',
    step3Subtitle: isGreek ? 'Ποιος είναι ο προϋπολογισμός και το χρονοδιάγραμμα' : 'Budget and timeline',
    step4Title: isGreek ? 'Μήνυμα' : 'Message',
    step4Subtitle: isGreek ? 'Περιγράψτε το έργο σας' : 'Tell us about your project',
    nextStep: isGreek ? 'Επόμενο' : 'Next',
    prevStep: isGreek ? 'Πίσω' : 'Back',
    submit: isGreek ? 'Υποβολή Αιτήματος' : 'Submit Request',
    required: isGreek ? '* Υποχρεωτικά πεδία' : '* Required fields',
  };

  const [currentStep, setCurrentStep] = useState<StepId>(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    message: '',
    services: [],
    budget: '',
    timeline: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const serviceOptions = [
    t('consultation.services.website'),
    t('consultation.services.uiux'),
    t('consultation.services.ecommerce'),
    t('consultation.services.marketing'),
    t('consultation.services.seo'),
    t('consultation.services.social'),
    t('consultation.services.mobile'),
    t('consultation.services.branding'),
    t('consultation.services.content'),
    t('consultation.services.email'),
  ];

  const budgetOptions = [
    t('consultation.budget.under1k'),
    t('consultation.budget.1k3k'),
    t('consultation.budget.3k5k'),
    t('consultation.budget.5k10k'),
    t('consultation.budget.over10k'),
    t('consultation.budget.discuss'),
  ];

  // Map Greek display values to Supabase enum values
  const mapBudgetToEnum = (budget: string): string => {
    const budgetMap: Record<string, string> = {
      'Κάτω από €1.000': 'under-1000',
      'Under €1.000': 'under-1000',
      '€1.000 - €3.000': '1000-5000',
      '€3.000 - €5.000': '3000-5000',
      '€5.000 - €10.000': '5000-10000',
      'Πάνω από €10.000': 'over-10000',
      'Over €10.000': 'over-10000',
      'Θα συζητήσουμε': 'discuss',
      "Let's discuss": 'discuss',
    };
    return budgetMap[budget] || budget;
  };

  const timelineOptions = [
    t('consultation.timeline.immediate'),
    t('consultation.timeline.soon'),
    t('consultation.timeline.medium'),
    t('consultation.timeline.long'),
    t('consultation.timeline.flexible'),
  ];

  const validateStep = (step: StepId): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;
    const fieldsToTouch: Record<string, boolean> = {};

    if (step === 1) {
      if (!formData.name.trim()) {
        newErrors.name = isGreek ? 'Το όνομα είναι υποχρεωτικό' : 'Name is required';
        isValid = false;
      }
      fieldsToTouch.name = true;

      if (!formData.email.trim()) {
        newErrors.email = isGreek ? 'Το email είναι υποχρεωτικό' : 'Email is required';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = isGreek ? 'Παρακαλώ εισάγετε ένα έγκυρο email' : 'Please enter a valid email';
        isValid = false;
      }
      fieldsToTouch.email = true;
    }

    if (step === 2) {
      if (formData.services.length === 0) {
        newErrors.services = isGreek ? 'Επιλέξτε τουλάχιστον μία υπηρεσία' : 'Select at least one service';
        isValid = false;
      }
      fieldsToTouch.services = true;
    }

    setErrors(newErrors);
    setTouched((prev) => ({ ...prev, ...fieldsToTouch }));
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleServiceChange = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
    if (errors.services) {
      setErrors((prev) => ({ ...prev, services: undefined }));
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => (prev + 1) as StepId);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => (prev - 1) as StepId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!validateStep(1)) {
      setCurrentStep(1);
      return;
    }
    if (!validateStep(2)) {
      setCurrentStep(2);
      return;
    }

    setIsSubmitting(true);

    try {
      // Append services and timeline to message since the API might not support them directly
      const servicesList = formData.services.join(', ');
      const timelineText = formData.timeline || 'Not specified';
      const augmentedMessage = `${formData.message}\n\nSelected Services: ${servicesList}\nTimeline: ${timelineText}`;

      const consultationData: ConsultationRequest = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        website: formData.website || undefined,
        message: augmentedMessage,
        services: formData.services,
        budget: formData.budget ? mapBudgetToEnum(formData.budget) : undefined,
        timeline: formData.timeline || undefined,
        status: 'new',
      };

      await insertConsultationRequest(consultationData);
      trackMetaPixelLead({
        content_name: 'Consultation Form',
        page_path: window.location.pathname
      });
      trackMetaPixelContact({
        channel: 'form',
        form_name: 'consultation',
        page_path: window.location.pathname
      });
      navigate(localizedPath('/consultation/thank-you'));
    } catch (error) {
      console.error('Error submitting consultation request:', error);
      setErrorMessage(copy.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between max-w-md mx-auto">
        {STEPS.map((step, index) => (
          <React.Fragment key={step}>
            {/* Step circle */}
            <div className="flex flex-col items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-sm transition-all duration-300 ${
                  step < currentStep
                    ? 'bg-brand-highlight text-white'
                    : step === currentStep
                    ? 'bg-brand-highlight/20 border-2 border-brand-highlight text-brand-highlight'
                    : 'bg-white/10 text-white/40 border-2 border-white/20'
                }`}
              >
                {step < currentStep ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  step
                )}
              </div>
              {/* Step label */}
              <span className={`mt-2 text-xs hidden md:block ${
                step === currentStep ? 'text-brand-highlight' : 'text-white/40'
              }`}>
                {step === 1 && t('consultation.step.contact')}
                {step === 2 && t('consultation.step.services')}
                {step === 3 && t('consultation.step.details')}
                {step === 4 && t('consultation.step.message')}
              </span>
            </div>
            {/* Connector line */}
            {index < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 transition-all duration-300 ${
                step < currentStep ? 'bg-brand-highlight' : 'bg-white/20'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  <User className="inline h-4 w-4 mr-2" />
                  {t('consultation.form.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className={`w-full rounded-lg border bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:ring-2 text-sm md:text-base ${
                    touched.name && errors.name
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-white/15 focus:border-brand-highlight/70 focus:ring-brand-highlight/60'
                  }`}
                  placeholder={t('consultation.form.name.placeholder')}
                />
                {touched.name && errors.name && (
                  <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  <Mail className="inline h-4 w-4 mr-2" />
                  {t('consultation.form.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className={`w-full rounded-lg border bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:ring-2 text-sm md:text-base ${
                    touched.email && errors.email
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-white/15 focus:border-brand-highlight/70 focus:ring-brand-highlight/60'
                  }`}
                  placeholder={t('consultation.form.email.placeholder')}
                />
                {touched.email && errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  <Phone className="inline h-4 w-4 mr-2" />
                  {t('consultation.form.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={t('consultation.form.phone.placeholder')}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  <Building className="inline h-4 w-4 mr-2" />
                  {t('consultation.form.company')}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={t('consultation.form.company.placeholder')}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <label className="mb-4 block text-sm font-medium text-white md:text-base">
                {t('consultation.form.services')} *
              </label>
              {errors.services && (
                <p className="mb-4 text-sm text-red-400">{errors.services}</p>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {serviceOptions.map((service) => {
                  const selected = formData.services.includes(service);
                  return (
                    <label key={service} className="group block cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => handleServiceChange(service)}
                        className="sr-only"
                      />
                      <div
                        className={`rounded-2xl border p-5 transition-all duration-200 ${
                          selected
                            ? 'border-brand-highlight/80 bg-brand-highlight/10 text-white shadow-glass'
                            : 'border-white/15 bg-black/70 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-full border transition ${
                              selected ? 'bg-brand-highlight text-white' : 'bg-transparent text-white/40'
                            }`}
                          >
                            <CheckCircle className="h-3 w-3" />
                          </div>
                          <span className="text-sm text-white/80 transition-colors group-hover:text-white">
                            {service}
                          </span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <label className="mb-2 block text-sm font-medium text-white md:text-base">
                <Globe className="inline h-4 w-4 mr-2" />
                {t('consultation.form.website')}
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                placeholder={t('consultation.form.website.placeholder')}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  {t('consultation.form.budget')}
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                >
                  <option value="">{t('consultation.form.budget.placeholder')}</option>
                  {budgetOptions.map((budget) => (
                    <option key={budget} value={budget} className="bg-black text-white">
                      {budget}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-white md:text-base">
                  {t('consultation.form.timeline')}
                </label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                >
                  <option value="">{t('consultation.form.timeline.placeholder')}</option>
                  {timelineOptions.map((timeline) => (
                    <option key={timeline} value={timeline} className="bg-black text-white">
                      {timeline}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 animate-fadeIn">
            <div>
              <label className="mb-2 block text-sm font-medium text-white md:text-base">
                <MessageSquare className="inline h-4 w-4 mr-2" />
                {t('consultation.form.message')} <span className="text-white/40">({isGreek ? 'Προαιρετικό' : 'Optional'})</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={6}
                className="w-full resize-none rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                placeholder={t('consultation.form.message.placeholder')}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return { title: copy.step1Title, subtitle: copy.step1Subtitle };
      case 2:
        return { title: copy.step2Title, subtitle: copy.endSubtitle };
      case 3:
        return { title: copy.step3Title, subtitle: copy.step3Subtitle };
      case 4:
        return { title: copy.step4Title, subtitle: copy.step4Subtitle };
      default:
        return { title: '', subtitle: '' };
    }
  };

  const stepInfo = getStepTitle();

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-10 flex items-center justify-between rounded-2xl border border-white/15 bg-black/70 px-4 py-4 shadow-glass md:px-6 md:py-6">
          <button
            onClick={() => window.navigateToHome?.()}
            className="flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors duration-200 hover:border-brand-highlight/60 hover:text-brand-highlight md:px-4 md:text-base"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t('consultation.back')}
          </button>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {t('consultation.title.label') ?? 'Free consultation'}
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-white md:text-4xl">
              {t('consultation.title')}
            </h1>
          </div>
          <div className="w-16 md:w-20" />
        </div>

        {/* Form */}
        <div className="rounded-[32px] border border-white/15 bg-black/70 p-4 shadow-glass md:p-10">
          {/* Step indicator */}
          {renderStepIndicator()}

          {/* Step header */}
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold text-white md:text-3xl">
              {stepInfo.title}
            </h2>
            <p className="mt-1 text-white/60">
              {stepInfo.subtitle}
            </p>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/20 p-4 text-center">
              <p className="text-red-400">{errorMessage}</p>
            </div>
          )}

          {/* Step content */}
          <div>{renderStepContent()}

            {/* Navigation buttons */}
            <div className="mt-8 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrevStep}
                disabled={currentStep === 1 || isSubmitting}
                className={`flex items-center rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 ${
                  currentStep === 1
                    ? 'cursor-not-allowed opacity-0'
                    : 'hover:border-brand-highlight/60 hover:text-brand-highlight'
                }`}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                {copy.prevStep}
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="flex items-center rounded-full border border-white/10 bg-white text-black px-6 py-3 text-sm font-semibold transition-all duration-300 hover:border-brand-highlight/70 hover:bg-brand-highlight hover:text-white"
                >
                  {copy.nextStep}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`flex min-w-[160px] items-center justify-center rounded-full border px-6 py-3 text-sm font-semibold transition-all duration-300 ${
                    isSubmitting
                      ? 'cursor-not-allowed border-white/10 bg-white/5 text-white/40'
                      : 'border-white/10 bg-white text-black hover:border-brand-highlight/70 hover:bg-brand-highlight hover:text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-black/40" />
                      {t('consultation.form.submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {copy.submit}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Required fields note */}
            <p className="mt-4 text-center text-sm text-white/50">
              {copy.required}
            </p>
          </div>
        </div>

        {/* Process steps */}
        <div className="mt-8 rounded-[32px] border border-white/15 bg-black/70 p-6 shadow-glass md:p-10">
          <h3 className="text-center text-xl font-semibold text-white md:text-2xl">
            {t('consultation.process.title')}
          </h3>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {['step1', 'step2', 'step3'].map((step, index) => (
              <div key={step} className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 text-center hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-brand-highlight/40 bg-brand-highlight/15 text-brand-highlight">
                  <span className="text-sm font-semibold">{index + 1}</span>
                </div>
                <h4 className="mt-4 text-sm font-semibold text-white md:text-base">
                  {t(`consultation.process.${step}.title`)}
                </h4>
                <p className="mt-2 text-sm text-white/70">
                  {t(`consultation.process.${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationPage;
